// Feedback Attachment Storage Service
// Handles image/screenshot uploads for feedback system
// Files are stored in a tenant-scoped bucket structure: {tenant_id}/{user_id}/{filename}

import { supabase } from '@/lib/supabase/client';

// ============================================================================
// CONFIGURATION
// ============================================================================

const BUCKET_NAME = 'feedback-attachments';

// Best practices for SaaS file attachments
const CONFIG = {
  maxFileSize: 10 * 1024 * 1024, // 10MB - reasonable limit for attachments
  maxFilesPerMessage: 10, // Limit attachments per message
  maxImageDimension: 2048, // Max width/height for images - preserve quality for screenshots
  imageQuality: 0.9, // High quality for screenshots (0-1)
  // Image types
  imageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'],
  // Document types
  documentTypes: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'text/plain',
    'text/csv'
  ],
  // All accepted types combined
  acceptedTypes: [
    // Images
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/gif',
    // Documents
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv'
  ],
  acceptedExtensions: [
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.gif',
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.txt',
    '.csv'
  ],
  cacheControl: '31536000' // 1 year cache (immutable content)
};

// Helper to check if file is an image
const isImageFile = (file: File | string): boolean => {
  const type = typeof file === 'string' ? file : file.type;
  return CONFIG.imageTypes.includes(type);
};

// Helper to check if file is a document
const isDocumentFile = (file: File | string): boolean => {
  const type = typeof file === 'string' ? file : file.type;
  return CONFIG.documentTypes.includes(type);
};

// ============================================================================
// TYPES
// ============================================================================

export interface FeedbackAttachment {
  id: string;
  filename: string;
  original_filename: string;
  content_type: string;
  size: number;
  url: string;
  path: string; // Storage path for deletion
  width?: number;
  height?: number;
  uploaded_at: string;
  file_type: 'image' | 'document'; // Categorize for UI display
}

export interface UploadProgress {
  filename: string;
  progress: number; // 0-100
  status: 'pending' | 'uploading' | 'compressing' | 'complete' | 'error';
  error?: string;
}

export interface UploadResult {
  success: boolean;
  attachment?: FeedbackAttachment;
  error?: string;
}

export interface BatchUploadResult {
  successful: FeedbackAttachment[];
  failed: { filename: string; error: string }[];
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validates a file before upload
 */
export function validateFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  if (!CONFIG.acceptedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type "${file.type}". Accepted: Images (JPEG, PNG, WebP, GIF), Documents (PDF, Word, Excel, TXT, CSV).`
    };
  }

  // Check file size
  if (file.size > CONFIG.maxFileSize) {
    const maxMB = CONFIG.maxFileSize / (1024 * 1024);
    return {
      valid: false,
      error: `File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds maximum of ${maxMB}MB.`
    };
  }

  // Check filename for suspicious patterns (executable files)
  const suspiciousPatterns = [
    /\.exe$/i,
    /\.bat$/i,
    /\.cmd$/i,
    /\.sh$/i,
    /\.php$/i,
    /\.js$/i,
    /\.vbs$/i,
    /\.ps1$/i,
    /\.msi$/i,
    /\.dll$/i,
    /\.scr$/i,
    /\.com$/i,
    /\.pif$/i,
    /\.jar$/i
  ];
  if (suspiciousPatterns.some(pattern => pattern.test(file.name))) {
    return {
      valid: false,
      error: 'Executable files are not allowed for security reasons.'
    };
  }

  return { valid: true };
}

/**
 * Validates multiple files
 */
export function validateFiles(files: File[]): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (files.length > CONFIG.maxFilesPerMessage) {
    errors.push(`Maximum ${CONFIG.maxFilesPerMessage} files allowed per message.`);
  }

  files.forEach(file => {
    const result = validateFile(file);
    if (!result.valid && result.error) {
      errors.push(`${file.name}: ${result.error}`);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
}

// ============================================================================
// IMAGE PROCESSING
// ============================================================================

/**
 * Gets image dimensions
 */
function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}

/**
 * Compresses and optionally resizes an image
 * Preserves quality for screenshots while reducing file size
 */
async function processImage(file: File): Promise<{ blob: Blob; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = event => {
      const img = new Image();

      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Only resize if exceeds max dimension
        if (width > CONFIG.maxImageDimension || height > CONFIG.maxImageDimension) {
          if (width > height) {
            if (width > CONFIG.maxImageDimension) {
              height = (height / width) * CONFIG.maxImageDimension;
              width = CONFIG.maxImageDimension;
            }
          } else {
            if (height > CONFIG.maxImageDimension) {
              width = (width / height) * CONFIG.maxImageDimension;
              height = CONFIG.maxImageDimension;
            }
          }
        }

        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // High-quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // For PNG screenshots, keep as PNG to preserve transparency and sharp text
        // For photos (JPEG), convert to JPEG with compression
        const isPNG = file.type === 'image/png';
        const outputType = isPNG ? 'image/png' : 'image/jpeg';
        const quality = isPNG ? undefined : CONFIG.imageQuality;

        canvas.toBlob(
          blob => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }
            resolve({ blob, width: Math.round(width), height: Math.round(height) });
          },
          outputType,
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = event.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// ============================================================================
// UPLOAD FUNCTIONS
// ============================================================================

/**
 * Generates a unique, safe filename
 */
function generateFilename(originalName: string, userId: string): string {
  const ext = originalName.split('.').pop()?.toLowerCase() || 'jpg';
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  // Use shorter user ID prefix for readability
  const userPrefix = userId.substring(0, 8);
  return `${userPrefix}-${timestamp}-${random}.${ext}`;
}

/**
 * Uploads a single feedback attachment (image or document)
 * @param tenantId - Tenant ID for bucket organization
 * @param userId - User ID for path organization
 * @param file - The file to upload (image or document)
 * @param conversationId - Optional conversation ID for organization
 */
export async function uploadFeedbackAttachment(
  tenantId: string,
  userId: string,
  file: File,
  conversationId?: string
): Promise<UploadResult> {
  try {
    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    const isImage = isImageFile(file);
    let processedBlob: Blob;
    let dimensions: { width: number; height: number } = { width: 0, height: 0 };
    let contentType = file.type;

    if (isImage) {
      // Process image (compress/resize if needed)
      try {
        const result = await processImage(file);
        processedBlob = result.blob;
        dimensions = { width: result.width, height: result.height };
        // Update content type based on processing
        contentType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      } catch (error) {
        // If processing fails, try uploading original
        console.warn('Image processing failed, using original:', error);
        processedBlob = file;
        try {
          dimensions = await getImageDimensions(file);
        } catch {
          dimensions = { width: 0, height: 0 };
        }
      }
    } else {
      // For documents, upload as-is without processing
      processedBlob = file;
    }

    // Generate unique filename
    const filename = generateFilename(file.name, userId);

    // Build storage path: {tenant_id}/{user_id}/{conversation_id?}/{filename}
    const pathParts = [tenantId, userId];
    if (conversationId) {
      pathParts.push(conversationId);
    }
    pathParts.push(filename);
    const storagePath = pathParts.join('/');

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(storagePath, processedBlob, {
        contentType,
        cacheControl: CONFIG.cacheControl,
        upsert: false
      });

    if (error) {
      // Handle bucket not found
      if (error.message.includes('Bucket not found') || error.message.includes('does not exist')) {
        return {
          success: false,
          error: `Storage bucket "${BUCKET_NAME}" not found. Please create it in Supabase Dashboard.`
        };
      }

      // Handle duplicate (retry with different name)
      if (error.message.includes('already exists')) {
        const retryFilename = generateFilename(file.name, userId);
        const retryPath = [...pathParts.slice(0, -1), retryFilename].join('/');

        const { data: retryData, error: retryError } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(retryPath, processedBlob, {
            contentType,
            cacheControl: CONFIG.cacheControl,
            upsert: false
          });

        if (retryError) {
          return { success: false, error: `Upload failed: ${retryError.message}` };
        }

        const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(retryPath);

        return {
          success: true,
          attachment: {
            id: crypto.randomUUID(),
            filename: retryFilename,
            original_filename: file.name,
            content_type: contentType,
            size: processedBlob.size,
            url: urlData.publicUrl,
            path: retryPath,
            width: dimensions.width,
            height: dimensions.height,
            uploaded_at: new Date().toISOString(),
            file_type: isImage ? 'image' : 'document'
          }
        };
      }

      return { success: false, error: `Upload failed: ${error.message}` };
    }

    if (!data) {
      return { success: false, error: 'Upload failed: No data returned' };
    }

    // Get public URL
    const { data: urlData } = supabase.storage.from(BUCKET_NAME).getPublicUrl(data.path);

    return {
      success: true,
      attachment: {
        id: crypto.randomUUID(),
        filename,
        original_filename: file.name,
        content_type: contentType,
        size: processedBlob.size,
        url: urlData.publicUrl,
        path: data.path,
        width: dimensions.width,
        height: dimensions.height,
        uploaded_at: new Date().toISOString(),
        file_type: isImage ? 'image' : 'document'
      }
    };
  } catch (error) {
    console.error('Upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    };
  }
}

/**
 * Uploads multiple feedback attachments
 */
export async function uploadFeedbackAttachments(
  tenantId: string,
  userId: string,
  files: File[],
  conversationId?: string,
  onProgress?: (progress: UploadProgress[]) => void
): Promise<BatchUploadResult> {
  const successful: FeedbackAttachment[] = [];
  const failed: { filename: string; error: string }[] = [];

  // Validate all files first
  const validation = validateFiles(files);
  if (!validation.valid) {
    return {
      successful: [],
      failed: validation.errors.map(error => ({ filename: '', error }))
    };
  }

  // Initialize progress
  const progress: UploadProgress[] = files.map(file => ({
    filename: file.name,
    progress: 0,
    status: 'pending'
  }));

  // Upload files sequentially to avoid overwhelming the server
  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    // Update progress
    progress[i] = { ...progress[i], status: 'compressing', progress: 20 };
    onProgress?.(progress);

    progress[i] = { ...progress[i], status: 'uploading', progress: 50 };
    onProgress?.(progress);

    const result = await uploadFeedbackAttachment(tenantId, userId, file, conversationId);

    if (result.success && result.attachment) {
      successful.push(result.attachment);
      progress[i] = { ...progress[i], status: 'complete', progress: 100 };
    } else {
      failed.push({ filename: file.name, error: result.error || 'Unknown error' });
      progress[i] = { ...progress[i], status: 'error', progress: 100, error: result.error };
    }

    onProgress?.(progress);
  }

  return { successful, failed };
}

/**
 * Deletes a feedback attachment
 */
export async function deleteFeedbackAttachment(
  path: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.storage.from(BUCKET_NAME).remove([path]);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete attachment'
    };
  }
}

/**
 * Deletes multiple feedback attachments
 */
export async function deleteFeedbackAttachments(
  paths: string[]
): Promise<{ success: boolean; error?: string }> {
  try {
    if (paths.length === 0) {
      return { success: true };
    }

    const { error } = await supabase.storage.from(BUCKET_NAME).remove(paths);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete attachments'
    };
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Gets the configuration for UI display
 */
export function getAttachmentConfig() {
  return {
    maxFileSize: CONFIG.maxFileSize,
    maxFileSizeMB: CONFIG.maxFileSize / (1024 * 1024),
    maxFilesPerMessage: CONFIG.maxFilesPerMessage,
    acceptedTypes: CONFIG.acceptedTypes,
    acceptedExtensions: CONFIG.acceptedExtensions.join(', ')
  };
}

/**
 * Formats file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default {
  uploadFeedbackAttachment,
  uploadFeedbackAttachments,
  deleteFeedbackAttachment,
  deleteFeedbackAttachments,
  validateFile,
  validateFiles,
  getAttachmentConfig,
  formatFileSize
};
